<template>
  <keep-alive>
    <component
      :is="isActive && 'TabKeeper'"
      :id="computedId"
      :aria-hidden="! isActive"
      class="tabs-component-panel h-full"
      role="tabpanel"
    >
      <div v-if="showHeader" class="w-full flex items-center justify-between px-5 py-2 relative">
        <div>
          <h2 class="font-medium text-gray-700 dark:text-gray-200">
            {{ name }}
          </h2>
          <p v-if="info" class="text-sm text-gray-600 font-normal dark:text-gray-300">
            {{ info }}
          </p>
        </div>

        <slot name="control" />
      </div>
      <slot />
    </component>
  </keep-alive>
</template>

<script setup lang="ts">
import TabKeeper from './TabKeeper.vue'

export interface TabRegistration {
  _isTab: boolean
  name: string
  icon: string | string[] | null
  alert: string | number | null
  tabClass: string
  isDisabled: boolean
  isVisible: boolean
  isActive: boolean
  initial: boolean
  scroll: boolean
  hash: string
  showHeader: boolean
}

interface Props {
  id?: string | null
  name: string
  info?: string | null
  icon?: string | string[] | null
  alert?: string | number | null
  prefix?: string
  suffix?: string
  tabClass?: string
  isDisabled?: boolean
  isVisible?: boolean
  initial?: boolean
  scroll?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  id: null,
  info: null,
  icon: null,
  alert: null,
  prefix: '',
  suffix: '',
  tabClass: '',
  isDisabled: false,
  isVisible: true,
  initial: false,
  scroll: false,
})

const isActive = ref(false)
const showHeader = ref(false)

const header = computed(() => props.prefix + props.name + props.suffix)

const computedId = computed(() => {
  return props.id ? props.id : props.name.toLowerCase().replace(/ /g, '-')
})

const hash = computed(() => {
  if (props.isDisabled) {
    return '#'
  }
  return '#' + computedId.value
})

// Registration object exposed to parent Tabs component
const registration = reactive<TabRegistration>({
  _isTab: true,
  name: props.name,
  icon: props.icon,
  alert: props.alert,
  tabClass: props.tabClass,
  isDisabled: props.isDisabled,
  isVisible: props.isVisible,
  isActive: isActive.value,
  initial: props.initial,
  scroll: props.scroll,
  hash: hash.value,
  showHeader: showHeader.value,
})

// Keep registration in sync with reactive state
watch(isActive, (val) => { registration.isActive = val })
watch(showHeader, (val) => { registration.showHeader = val })
watch(hash, (val) => { registration.hash = val })
watch(() => props.name, (val) => { registration.name = val })
watch(() => props.icon, (val) => { registration.icon = val })
watch(() => props.alert, (val) => { registration.alert = val })
watch(() => props.tabClass, (val) => { registration.tabClass = val })
watch(() => props.isDisabled, (val) => { registration.isDisabled = val })
watch(() => props.isVisible, (val) => { registration.isVisible = val })
watch(() => props.initial, (val) => { registration.initial = val })
watch(() => props.scroll, (val) => { registration.scroll = val })

// Sync parent mutations back to local refs
watch(() => registration.isActive, (val) => { isActive.value = val })
watch(() => registration.showHeader, (val) => { showHeader.value = val })

// Inject register/unregister from parent Tabs
const register = inject<((tab: TabRegistration) => void) | null>('tabs-register', null)
const unregister = inject<((tab: TabRegistration) => void) | null>('tabs-unregister', null)

onMounted(() => {
  register?.(registration)
})

onBeforeUnmount(() => {
  unregister?.(registration)
})
</script>
